import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import clientApi, {
  IClientApiResponse,
  mapCreatorsFromApiModelToViewModel,
} from '~/clientApi';
import { Creator } from '~/clientApi/creator.model';
import useForbiddenQuery from '~/hooks/useForbiddenQuery';
import { SAVED_LIST_CREATORS } from '~/queries/keys';
import { ICreator } from '~/types';

const SHARED_SAVED_LIST_CREATORS_PAGE_SIZE = 10;

interface Params {
  id: string;
  pageSize?: number;
  sharedToken?: string;
  filters?: string[];
  sortField?: string;
  sortDirection?: string;
}

export const useGetSharedSavedListCreators = ({
  id,
  sharedToken,
  pageSize = SHARED_SAVED_LIST_CREATORS_PAGE_SIZE,
  filters,
  sortField,
  sortDirection,
}: Params) => {
  const query = useForbiddenQuery(
    useInfiniteQuery<
      IClientApiResponse<Creator>,
      Error,
      { creators: ICreator[]; totalItems: number },
      any
    >({
      queryKey: [SAVED_LIST_CREATORS, id, filters, sortField, sortDirection],
      queryFn: ({ pageParam = 1 }) =>
        clientApi.savedLists.creators({
          id,
          _sharedToken: sharedToken,
          pageSize,
          page: pageParam,
          // @ts-ignore
          'filter[]': filters,
          'sort[]': `${sortField}:${sortDirection}`,
        }),
      enabled: !!id && !!sharedToken,
      retry: 0,
      getNextPageParam: (page, pages) =>
        Math.floor(pages.length * pageSize) <= (page?.totalItems ?? 0)
          ? pages.length + 1
          : undefined,
      keepPreviousData: true,
    }),
  );

  const mappedData = useMemo(() => {
    const rawPages = query.data?.pages ?? [];
    const pages = rawPages.reduce(
      (acc: IClientApiResponse<Creator>, page: IClientApiResponse<Creator>) => {
        acc.results = [...acc.results, ...page.results];
        acc.totalItems = page.totalItems;

        return acc;
      },
      {
        results: [],
        totalItems: 0,
      },
    );

    return {
      creators: mapCreatorsFromApiModelToViewModel(pages.results),
      totalItems: pages.totalItems,
    };
  }, [query.data]);

  return {
    ...query,
    data: mappedData,
    originalQuery: query,
  };
};
